import { notifications } from "@mantine/notifications";
import type { ReactNode } from "react";

/**
 * Meta for a mutation whose form owns error UX via handleFormErrors.
 * The global MutationCache handler (lib/mutationCache.ts) skips these so
 * the form's catch is the sole owner — inline field errors for a mappable
 * 400, one generic toast for anything else. Spread into useMutation:
 *
 *   useMutation({ mutationFn, meta: FORM_MUTATION_META })
 *
 * US-081. Grep for FORM_MUTATION_META to enumerate every opted-out mutation.
 */
export const FORM_MUTATION_META = { suppressErrorNotification: true } as const;

/**
 * Extracts the most useful error message from an API call failure.
 *
 * Priority:
 *  1. response.data.error  — structured backend message (most useful)
 *  2. error.message        — Axios generic ("Request failed with status code 4xx")
 *  3. fallback             — caller-supplied default
 *
 * IMPORTANT: Do not check `error instanceof Error` first — AxiosError extends
 * Error, so that branch always wins and swallows the backend message.
 */
export function extractApiError(
  err: unknown,
  fallback = "An error occurred",
): string {
  return (
    (err as { response?: { data?: { error?: string } } })?.response?.data
      ?.error ||
    (err instanceof Error ? err.message : null) ||
    fallback
  );
}

/**
 * Flatten a backend validation `details` map into one human-readable string.
 *
 * handleValidationError on the backend always sets the top-level `error` to the
 * generic "Validation failed" and puts the real, per-field reasons in `details`
 * (Record<field, messages[]>). A toast that reads only `error` therefore tells
 * the user nothing actionable — the symptom in SCRUM-124, where a clock-out
 * correction blocked by "Cannot be in the future" surfaced only as a generic
 * "Validation failed". This pulls the actual messages out.
 *
 * Returns null when there are no usable details, so callers can fall through to
 * the generic `error` / fallback chain.
 */
export function extractValidationDetails(err: unknown): string | null {
  const details = (
    err as { response?: { data?: { details?: Record<string, string[]> } } }
  )?.response?.data?.details;
  if (!details) return null;
  const messages = Object.values(details)
    .flat()
    .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
  return messages.length > 0 ? messages.join(" · ") : null;
}

/** Structural interface — any Mantine form satisfies this. */
interface MantineFormLike {
  setFieldError: (field: string, error: ReactNode) => void;
  /**
   * Present on real Mantine forms. Enables the map-or-toast split: a detail key
   * whose root matches a form value is shown inline; one that doesn't (e.g. a
   * dose-limit reason keyed `prn`, which no input renders) is surfaced as a
   * toast instead of being silently swallowed.
   *
   * Typed as `object` (not `Record<string, unknown>`) so a form whose values are
   * a concrete shape still satisfies the structural type; only `Object.keys` is
   * read off it.
   */
  getValues?: () => object;
}

/**
 * Bring the first invalid field into view after a blocked submit.
 *
 * US-080 surfaces validation INLINE on the field, never as a generic toast —
 * that's the anti-pattern the story removes. But a submit blocked by a field
 * scrolled off-screen looks like a dead button. This points the user at the
 * problem the US-080 way: scroll the first errored field into view and focus
 * it, rather than popping a toast.
 *
 * Drop-in as Mantine's `form.onSubmit(onValid, onInvalid)` second argument, and
 * safe to call from manual array-row guards too. Every Mantine input with an
 * `error` prop renders `aria-invalid="true"`, so this matches both Mantine
 * field errors and manually-set row errors. Deferred a macrotask so React has
 * committed the error state to the DOM before we query it.
 */
export function scrollToFirstError(): void {
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!el) return;
    // Optional-chained: jsdom (test env) doesn't implement scrollIntoView.
    el.scrollIntoView?.({ behavior: "smooth", block: "center" });
    el.focus?.({ preventScroll: true });
  }, 0);
}

/**
 * Backend API error response structure
 */
interface ApiErrorResponse {
  response?: {
    data?: {
      error?: string;
      details?: Record<string, string[]>;
    };
  };
}

/**
 * Handle backend validation errors and display them on form fields
 *
 * @param error - The caught error from API call
 * @param form - Mantine form instance
 * @returns true if error was handled, false if should fall through to generic handler
 *
 * @example
 * ```typescript
 * try {
 *   await onSubmit(values);
 * } catch (error) {
 *   if (handleFormValidationErrors(error, form)) {
 *     return; // Error handled, stop here
 *   }
 *   // Handle other errors...
 * }
 * ```
 */
export function handleFormValidationErrors(
  error: unknown,
  form: MantineFormLike,
): boolean {
  const apiError = error as ApiErrorResponse;

  // Check if this is a validation error with field-specific details
  if (apiError?.response?.data?.details) {
    const details = apiError.response.data.details;

    // Map-or-toast (US-081): a detail key with a field to point at is surfaced
    // inline; a key that maps to no form field (e.g. the administer route's
    // `prn` dose-limit reason) has no input to render on, so it must NOT be set
    // silently on a phantom field — it's collected and shown as a toast instead.
    // Without field introspection (a minimal form lacking getValues) we keep the
    // legacy behavior and set every key inline.
    const knownRoots = form.getValues
      ? new Set(Object.keys(form.getValues()))
      : null;
    const unmatched: string[] = [];

    Object.entries(details).forEach(([field, errors]) => {
      const message = errors[0]; // Show first error for each field
      const mappable =
        knownRoots === null || knownRoots.has(field.split(/[.[]/)[0]);
      if (mappable) {
        form.setFieldError(field, message);
      } else if (typeof message === "string" && message.trim().length > 0) {
        unmatched.push(message);
      }
    });

    if (unmatched.length > 0) {
      notifications.show({
        title: "Error",
        message: unmatched.join(" · "),
        color: "red",
      });
    }

    return true; // Error was handled (inline and/or toast)
  }

  return false; // Not a validation error, caller should handle
}

/**
 * Handle generic API errors with user-friendly messages
 *
 * @param error - The caught error from API call
 * @param defaultMessage - Fallback message if error cannot be parsed
 *
 * @example
 * ```typescript
 * try {
 *   await onSubmit(values);
 * } catch (error) {
 *   if (handleFormValidationErrors(error, form)) {
 *     return;
 *   }
 *   handleGenericFormError(error, 'Failed to save resident');
 * }
 * ```
 */
export function handleGenericFormError(
  error: unknown,
  defaultMessage: string = "An error occurred",
): void {
  const apiError = error as ApiErrorResponse;

  // Prefer the structured error message from the API response body.
  // AxiosError extends Error, so checking instanceof Error first would
  // always swallow the backend message in favour of the generic Axios one.
  const errorMessage =
    apiError?.response?.data?.error ||
    (error instanceof Error ? error.message : null) ||
    defaultMessage;

  notifications.show({
    title: "Error",
    message: errorMessage,
    color: "red",
  });

  console.error("Form error:", error);
}

/**
 * Complete error handler that combines validation and generic error handling
 *
 * @param error - The caught error from API call
 * @param form - Mantine form instance
 * @param defaultMessage - Fallback message for generic errors
 *
 * @example
 * ```typescript
 * try {
 *   await onSubmit(values);
 * } catch (error) {
 *   handleFormErrors(error, form, 'Failed to save resident');
 * }
 * ```
 */
export function handleFormErrors(
  error: unknown,
  form: MantineFormLike,
  defaultMessage: string = "An error occurred",
): void {
  // Try to handle as validation error first
  if (handleFormValidationErrors(error, form)) {
    return;
  }

  // Fall back to generic error handling
  handleGenericFormError(error, defaultMessage);
}
